import type { QueryClient } from "@tanstack/react-query";
import { PBJ_MODEL_QUERY_KEY } from "./usePbjModel";

/**
 * Shared staleness policy for PBJ Model caches that are advanced by explicit user
 * actions (Calculate, the Jobs/Breaks save mutations, manual-hours writes) rather
 * than by the passage of time. Mirrors the exact policy `usePbjPrediction` has always
 * used, so every mutation-driven PBJ cache behaves identically: never auto-refetch on
 * focus / mount / reconnect. The only things that refresh these caches are the
 * mutations' own `setQueryData` / `invalidateQueries` (invalidation still refetches
 * active observers regardless of `staleTime`), which keeps switching tabs or windows
 * from silently refiring server calls.
 */
export const pbjCachedQueryOptions = {
	staleTime: Number.POSITIVE_INFINITY,
	refetchOnWindowFocus: false,
	refetchOnMount: false,
	refetchOnReconnect: false,
} as const;

/** Prefix shared by every committed-window PBJ prediction job-coverage query. */
export const PBJ_PREDICTION_JOB_COVERAGE_QUERY_KEY = [PBJ_MODEL_QUERY_KEY, "prediction-job-coverage"] as const;

/** Retire all cached coverage windows after a successful actual shift/job write. */
export const invalidatePbjPredictionJobCoverage = (queryClient: QueryClient): void => {
	void queryClient.invalidateQueries({ queryKey: PBJ_PREDICTION_JOB_COVERAGE_QUERY_KEY });
};
